import { Anchor, Center, Title } from "@mantine/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { createColumnHelper } from "@tanstack/react-table";
import { IconCircleCheck, IconCircleDotted } from "@tabler/icons-react";
import type { ProspectInList } from "@/models/ProspectInList";
import { DataTable } from "@/components/DataTable";
import { getProspectsListForUserQuery } from "@/api";
import { getNameForDisplay } from "@/utils/getNameForDisplay";

export const Route = createFileRoute("/prospects/")({
  component: Prospects,
  loader: async ({ context }) => {
    const { secureAxios } = context.apiHook;
    const prospects = await context.queryClient.fetchQuery(
      getProspectsListForUserQuery(secureAxios)
    );

    return {
      prospects
    };
  }
});

function Prospects() {
  const { prospects } = Route.useLoaderData();
  const { userClaim } = Route.useRouteContext();

  const communityClaims =
    userClaim?.claims?.filter((claim) => claim.type === "for-community") || [];
  const shouldShowCommunityColumn = communityClaims.length > 1;

  const columnHelper = createColumnHelper<ProspectInList>();
  const getChecklistColumnContents = (status: boolean) => (
    <Center>
      {status ? (
        <IconCircleCheck size={17} color="var(--mantine-color-green-8)" />
      ) : (
        <IconCircleDotted size={17} color="var(--mantine-color-warmGray-3)" />
      )}
    </Center>
  );

  return (
    <div>
      <Title order={3}>My Prospects</Title>
      <DataTable
        columns={[
          columnHelper.accessor((row) => getNameForDisplay(row), {
            id: "name",
            header: "Prospect Name",
            cell: (info) => {
              const prospect = info.row.original;
              const fullName = info.getValue();
              return (
                <Anchor component={Link} to={`/prospects/${prospect.id}`}>
                  {fullName}
                </Anchor>
              );
            },
            sortingFn: (a, b) => {
              const aLast = a.original.lastName.toLowerCase();
              const bLast = b.original.lastName.toLowerCase();
              if (aLast < bLast) {
                return -1;
              }
              if (aLast > bLast) {
                return 1;
              }
              const aFirst = a.original.firstName.toLowerCase();
              const bFirst = b.original.firstName.toLowerCase();
              if (aFirst < bFirst) {
                return -1;
              }
              if (aFirst > bFirst) {
                return 1;
              }
              return 0;
            }
          }),
          // Conditionally include Community column
          ...(shouldShowCommunityColumn
            ? [
                columnHelper.accessor("communityName", {
                  header: "Community"
                })
              ]
            : []),
          columnHelper.accessor("initialContactAt", {
            header: "Inquiry Date",
            cell: ({ getValue }) => {
              const value = getValue<string>();
              return value ? (
                <span className="whitespace-nowrap">
                  {DateTime.fromISO(value).toFormat("yyyy-MM-dd")}
                </span>
              ) : (
                "--"
              );
            }
          }),
          columnHelper.accessor("stageId", {
            header: "Stage",
            meta: {
              style: { whiteSpace: "nowrap" }
            },
            cell: ({ row }) => row.original.stageName
          }),
          columnHelper.accessor("docInterviewComplete", {
            header: "DoC Interview",
            cell: ({ getValue }) => getChecklistColumnContents(getValue())
          }),
          columnHelper.accessor("teaAndChatComplete", {
            header: "Tea and Chat",
            cell: ({ getValue }) => getChecklistColumnContents(getValue())
          }),
          columnHelper.accessor("finalFollowUpComplete", {
            header: "Final Follow-Up",
            cell: ({ getValue }) => getChecklistColumnContents(getValue())
          }),
          columnHelper.accessor("decisionMade", {
            header: "Decision Made",
            cell: ({ getValue }) => getChecklistColumnContents(getValue())
          }),
          columnHelper.accessor("movedIn", {
            header: "Moved In",
            cell: ({ getValue }) => getChecklistColumnContents(getValue())
          }),
          columnHelper.accessor("lifeStoryGenerated", {
            header: "Life Story",
            cell: ({ getValue }) => getChecklistColumnContents(getValue())
          })
        ]}
        data={prospects}
      />
    </div>
  );
}
